import React from "react";
import { Workflow, Truck, DollarSign, MessageSquare, Send } from "lucide-react";
import { Modal } from "./Modal";
import { SubcontractorTypeahead } from "./SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

function Field({ label, hint, required, children }) {
  return (
    <label style={{ display: "block", marginBottom: 12 }}>
      <span style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--text-3)", marginBottom: 4 }}>
        {label}{required && <span style={{ color: "#c0303a" }}> *</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3, display: "block" }}>{hint}</span>}
    </label>
  );
}

// ----- 1. Subcontractor drop modal -----------------------------------------
export function SubconDropModal({ card, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    subcontractor_name: card?.subcontractor_name || "",
    subcontractor_reference: card?.subcontractor_reference || "",
    sent_date: card?.sent_date || today,
    expected_return_date: card?.expected_return_date || "",
    reason_for_subcontracting: card?.reason_for_subcontracting || "",
  });
  const [busy, setBusy] = React.useState(false);

  const valid = form.subcontractor_name && form.sent_date;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.patch(`/job-items/${card.id}/status`, { status: "at_subcontractor", ...form });
      toast.success("Sent to subcontractor");
      onDone?.();
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Workflow} title="Send to subcontractor"
      subtitle={`${card?.description || "Item"} · ${card?.delivery_note_number || ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="subcon-cancel">Cancel</button>
          <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="subcon-submit">
            <Send size={14} /> Send to subcontractor
          </button>
        </>
      }
    >
      <Field label="Subcontractor" required>
        <SubcontractorTypeahead
          value={form.subcontractor_name}
          onChange={(v) => setForm({ ...form, subcontractor_name: v })}
          placeholder="Start typing — pick from history or enter new"
          testid="subcon-name" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Sent date" required>
          <input type="date" className="input" data-testid="subcon-sent-date" value={form.sent_date}
            onChange={(e) => setForm({ ...form, sent_date: e.target.value })} />
        </Field>
        <Field label="Expected return">
          <input type="date" className="input" data-testid="subcon-return-date" value={form.expected_return_date}
            onChange={(e) => setForm({ ...form, expected_return_date: e.target.value })} />
        </Field>
      </div>
      <Field label="Their reference / job no.">
        <input className="input" data-testid="subcon-reference" value={form.subcontractor_reference}
          onChange={(e) => setForm({ ...form, subcontractor_reference: e.target.value })}
          placeholder="e.g. NMISA-2026-1042" />
      </Field>
      <Field label="Reason for subcontracting">
        <textarea className="input" rows={2} data-testid="subcon-reason" value={form.reason_for_subcontracting}
          onChange={(e) => setForm({ ...form, reason_for_subcontracting: e.target.value })}
          placeholder="Out of scope / accredited cal required / etc." />
      </Field>
    </Modal>
  );
}

// ----- 2. Ready-for-Dispatch (cert capture) --------------------------------
export function DispatchReadyModal({ card, onClose, onDone }) {
  const [form, setForm] = React.useState({
    calibration_result: card?.calibration_result || "passed",
    certificate_number: card?.certificate_number || "",
    technician_notes: "",
  });
  const [busy, setBusy] = React.useState(false);

  const valid = !!form.calibration_result;
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.patch(`/job-items/${card.id}/status`, { status: "ready_for_delivery", ...form });
      toast.success("Marked In Dispatch");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Truck} title="In Dispatch — capture outcome"
      subtitle={`${card?.description || "Item"} · ${card?.delivery_note_number || ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="dispatch-cancel">Cancel</button>
          <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="dispatch-submit">Mark Ready</button>
        </>
      }
    >
      <Field label="Calibration result" required>
        <select className="input" data-testid="dispatch-result" value={form.calibration_result}
          onChange={(e) => setForm({ ...form, calibration_result: e.target.value })}>
          <option value="passed">Passed</option>
          <option value="passed_with_adjustment">Passed with adjustment</option>
          <option value="failed">Failed</option>
          <option value="returned_no_work">Returned without work</option>
          <option value="unable_to_calibrate">Unable to calibrate</option>
        </select>
      </Field>
      <Field label="Certificate number" hint="Leave blank to auto-generate the next certificate on issue.">
        <input className="input" data-testid="dispatch-cert" value={form.certificate_number}
          onChange={(e) => setForm({ ...form, certificate_number: e.target.value })}
          placeholder="e.g. PTE-2026-04217" />
      </Field>
      <Field label="Technician completion note">
        <textarea className="input" rows={2} data-testid="dispatch-notes" value={form.technician_notes}
          onChange={(e) => setForm({ ...form, technician_notes: e.target.value })} />
      </Field>
    </Modal>
  );
}

// ----- 3. Invoice / Awaiting-Payment capture -------------------------------
export function InvoiceModal({ card, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = React.useState({
    invoice_number: card?.invoice_number || "",
    invoice_date: card?.invoice_date || today,
    invoice_amount: card?.invoice_amount || "",
    note: "",
  });
  const [busy, setBusy] = React.useState(false);
  const valid = !!form.invoice_number.trim();

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.patch(`/job-items/${card.id}/payment`, {
        payment_status: "awaiting_payment",
        invoice_number: form.invoice_number.trim(),
        invoice_date: form.invoice_date,
        invoice_amount: form.invoice_amount ? Number(form.invoice_amount) : undefined,
        note: form.note || undefined,
      });
      toast.success("Invoice captured — awaiting payment");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={DollarSign} title="Awaiting Payment — capture invoice"
      subtitle={`${card?.description || "Item"} · ${card?.delivery_note_number || ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="invoice-cancel">Cancel</button>
          <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="invoice-submit">Capture &amp; flag awaiting payment</button>
        </>
      }
    >
      <Field label="Sage invoice number" required hint="The invoice reference from Sage / your accounts system.">
        <input className="input" data-testid="invoice-number" value={form.invoice_number}
          onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
          placeholder="e.g. INV-2026-08412" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Invoice date">
          <input type="date" className="input" data-testid="invoice-date" value={form.invoice_date}
            onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} />
        </Field>
        <Field label="Amount (R)">
          <input type="number" step="0.01" min="0" className="input" data-testid="invoice-amount" value={form.invoice_amount}
            onChange={(e) => setForm({ ...form, invoice_amount: e.target.value })} />
        </Field>
      </div>
      <Field label="Note">
        <input className="input" data-testid="invoice-note" value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </Field>
    </Modal>
  );
}

// ----- 4. Customer-facing issue modal --------------------------------------
export function CustomerIssueModal({ card, onClose, onDone }) {
  const [form, setForm] = React.useState({
    customer_question: "",
    recommended_action: "",
    customer_contact: "",
    notify_customer: true,
  });
  const [busy, setBusy] = React.useState(false);
  const valid = form.customer_question.trim().length >= 4;

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await api.patch(`/job-items/${card.id}/status`, {
        status: "awaiting_customer_approval", ...form,
      });
      toast.success("Awaiting customer feedback");
      onDone?.();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={MessageSquare} title="Request customer input"
      subtitle={`${card?.description || "Item"} · ${card?.delivery_note_number || ""}`}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="cust-issue-cancel">Cancel</button>
          <button className="btn gold" onClick={submit} disabled={!valid || busy} data-testid="cust-issue-submit">Send to customer</button>
        </>
      }
    >
      <Field label="Issue description" required hint="What the customer needs to know — written in customer-facing language.">
        <textarea className="input" rows={3} data-testid="cust-issue-desc" value={form.customer_question}
          onChange={(e) => setForm({ ...form, customer_question: e.target.value })}
          placeholder="e.g. The transducer reads 4% high across the upper range — replacement is recommended." />
      </Field>
      <Field label="Recommended action">
        <input className="input" data-testid="cust-issue-action" value={form.recommended_action}
          onChange={(e) => setForm({ ...form, recommended_action: e.target.value })}
          placeholder="Approve repair / replace / scrap / collect as-is" />
      </Field>
      <Field label="Customer contact for this decision">
        <input className="input" data-testid="cust-issue-contact" value={form.customer_contact}
          onChange={(e) => setForm({ ...form, customer_contact: e.target.value })}
          placeholder="Name or email" />
      </Field>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, marginTop: 8 }}>
        <input type="checkbox" data-testid="cust-issue-notify" checked={form.notify_customer}
          onChange={(e) => setForm({ ...form, notify_customer: e.target.checked })} />
        Send a portal notification to the customer immediately
      </label>
    </Modal>
  );
}
